// src/components/reports/PinnedMeasureCard.tsx — the "From Reports" Home slice.
// A compact live data card for the single pinned measure, rendered at the very
// bottom of Home (only for users with apps.reports). Ported from the prototype
// reports-shell.jsx PinnedBody + PinnedMeasureStrip.

import type { ReactNode } from "react";
import { C, F } from "../../theme";
import { Card } from "../ui/Card";
import { Section } from "../ui/Section";
import { Stamp } from "../ui/Stamp";
import { RIcon } from "./reportsIcons";
import { Trend } from "./Trend";
import { RevenueBars } from "./RevenueBars";
import { CapacityGauge } from "./CapacityGauge";
import { MEASURES, fmtGBP, fmtNum, yoy } from "../../lib/reports";
import type { MeasureId, ReportsSnapshot } from "../../lib/reports";

const Big = ({ children }: { children: ReactNode }) => (
  <span style={{ fontFamily: F.serif, fontSize: 30, fontWeight: 800, color: C.ink, letterSpacing: -0.6, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{children}</span>
);
const Cap = ({ children }: { children: ReactNode }) => (
  <div style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.inkMute, marginTop: 5, letterSpacing: 0.3, textTransform: "uppercase" }}>{children}</div>
);

const PinnedBody = ({ id, s }: { id: MeasureId; s: ReportsSnapshot }) => {
  switch (id) {
    case "net-revenue":
      return (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10 }}>
          <div><Big>{fmtGBP(s.netRevenue.fyToDate, { compact: true })}</Big><Cap>{s.fyLabel} · net to date</Cap></div>
          <Trend delta={yoy(s.netRevenue.fyToDate, s.netRevenue.fyPrevToDate)} />
        </div>
      );
    case "gift-aid":
      return (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10 }}>
          <div><Big>{fmtGBP(s.giftAid.fyToDate, { compact: true })}</Big><Cap>Gift Aid · {s.fyLabel}</Cap></div>
          <Trend delta={yoy(s.giftAid.fyToDate, s.giftAid.fyPrevToDate)} />
        </div>
      );
    case "footfall":
      return (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10 }}>
          <div><Big>{fmtNum(s.footfall.fyToDate)}</Big><Cap>Passengers · {s.fyLabel}</Cap></div>
          <Trend delta={yoy(s.footfall.fyToDate, s.footfall.fyPrevToDate)} />
        </div>
      );
    case "revenue-yoy":
      return <RevenueBars data={s.revenueByMonth} h={130} />;
    case "pullman-net":
      return (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10 }}>
          <div><Big>{fmtGBP(s.pullman.netFyToDate, { compact: true })}</Big><Cap>Pullman net · {s.fyLabel}</Cap></div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 700, color: C.inkSoft, letterSpacing: -0.3, fontVariantNumeric: "tabular-nums" }}>{fmtGBP(s.pullman.netThisMonth, { compact: true })}</div>
            <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.inkMute, marginTop: 3 }}>{s.pullman.monthLabel} so far</div>
          </div>
        </div>
      );
    case "pullman-capacity":
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <CapacityGauge value={s.pullman.capacityNext30} size={104} />
          <div style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.inkSoft, lineHeight: 1.5 }}>Pullman seats sold,<br />next 30 days</div>
        </div>
      );
    case "events-net":
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
          <div style={{ paddingRight: 12 }}><Stamp tone="heather">LAST 30</Stamp><div style={{ marginTop: 6 }}><Big>{fmtGBP(s.events.last30Net, { compact: true })}</Big></div></div>
          <div style={{ paddingLeft: 12, borderLeft: `1px solid ${C.hairlineLt}` }}><Stamp tone="sage">UPCOMING</Stamp><div style={{ marginTop: 6 }}><Big>{fmtGBP(s.events.next30Net, { compact: true })}</Big></div></div>
        </div>
      );
    default:
      return null;
  }
};

export const PinnedMeasureStrip = ({
  pinnedId,
  snapshot,
  onOpenReports,
  onUnpin,
}: {
  pinnedId: MeasureId | null;
  snapshot: ReportsSnapshot | null;
  onOpenReports: () => void;
  onUnpin: () => void;
}) => {
  if (!pinnedId || !snapshot) return null;
  const m = MEASURES.find((x) => x.id === pinnedId);
  if (!m) return null;
  return (
    <div>
      <Section
        title="From Reports"
        sub="Your pinned measure"
        right={
          <button type="button" onClick={onOpenReports} style={{ background: "none", border: "none", fontFamily: F.body, fontWeight: 700, fontSize: 13, color: C.brassDk, cursor: "pointer", letterSpacing: 0.3 }}>
            Open Reports →
          </button>
        }
      />
      <Card padded={false} edge={false} onClick={onOpenReports} style={{ overflow: "hidden", cursor: "pointer" }}>
        <div style={{ height: 3, background: `linear-gradient(90deg, ${C.brassDk}, ${C.brassLt} 70%, transparent)` }} />
        <div style={{ padding: "13px 15px 0", display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: C.brassBg, border: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <RIcon.HomePin s={16} c={C.brassDk} />
          </div>
          <div style={{ flex: 1, fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink, letterSpacing: -0.2 }}>{m.label}</div>
          <button type="button" onClick={(e) => { e.stopPropagation(); onUnpin(); }} aria-label="Unpin" style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 800, color: C.inkMute, background: C.paperWarm, border: `1px solid ${C.hairlineLt}`, borderRadius: 8, padding: "5px 9px", cursor: "pointer" }}>
            Unpin
          </button>
        </div>
        <div style={{ padding: "11px 15px 15px" }}><PinnedBody id={pinnedId} s={snapshot} /></div>
      </Card>
    </div>
  );
};
